import { forwardRef } from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  Box,
  InputLabel,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface InputProps extends Omit<TextFieldProps, 'variant' | 'label'> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  label?: string;
  sx?: SxProps<Theme>;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ leftIcon, rightIcon, label, fullWidth = true, sx, ...props }, ref) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {label && (
          <InputLabel
            htmlFor={props.id}
            error={props.error}
            required={props.required}
          >
            {label}
          </InputLabel>
        )}
        <TextField
          inputRef={ref}
          variant="outlined"
          fullWidth={fullWidth}
          sx={[
            {
              // Additional custom styles can go here if needed beyond theme defaults
              '& .MuiInputAdornment-root': {
                color: 'text.secondary',
                '&.MuiInputAdornment-positionStart': {
                  marginRight: 2,
                  marginLeft: 0.5,
                },
                '&.MuiInputAdornment-positionEnd': {
                  marginLeft: 2,
                  marginRight: 0.5,
                },
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...props}
          slotProps={{
            inputLabel: { shrink: true }, // This will be hidden since we're not passing label
            input: {
              startAdornment: leftIcon ? (
                <InputAdornment position="start">{leftIcon}</InputAdornment>
              ) : undefined,
              endAdornment: rightIcon ? (
                <InputAdornment position="end">{rightIcon}</InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
    );
  }
);

Input.displayName = 'Input';

export default Input;
